/**
 * Redux reducer for the GlobalModal component
 * Manages modal state including visibility and content
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModalMessage, ModalState } from '../../interfaces/connected-components/globalModal';

/**
 * Initial state for the modal reducer
 * Modal is initially hidden with empty content
 */
const initialState: ModalState = {
  isVisible: false,
  message: '',
  title: '',
  type: 'info',
  onConfirm: null,
  onReject: null,
};

/**
 * Redux slice for modal state management
 * Provides reducers for showing and hiding modals
 */
export const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    /**
     * Reducer to show the modal with specified content and options
     *
     * @param state - Current modal state
     * @param action - Action with modal configuration payload
     */
    showModal: (state, action: PayloadAction<ModalMessage>) => {
      state.title = action.payload.title;
      state.message = action.payload.message || '';
      state.isVisible = true;
      state.type = action.payload.type;
      state.onConfirm = action.payload.onConfirm;
      state.onReject = action.payload.onReject;
      state.customTopComponent = action.payload.customTopComponent;
      state.confirmText = action.payload.confirmText;
      state.rejectText = action.payload.rejectText;
    },
    /**
     * Reducer to hide the modal without changing its content
     *
     * @param state - Current modal state
     */
    hideModal: (state) => {
      state.isVisible = false;
    },
  },
});

// Action creators are generated for each case reducer function
export const { showModal, hideModal } = modalSlice.actions;

export default modalSlice.reducer;
